import { SkyText } from "@skypointcloud/skypoint-ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import type { McpAdminGrantView, McpGrantList, McpGrantView } from "@shared/mcp/consent";
import { ZoneCaption } from "@/components/shell/ZoneCaption";
import { useRevokeMcpGrant, type McpRevokeScope } from "@/hooks/use-mcp";
import { useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { McpGrantsGrid } from "./McpGrantsGrid";
import { RevokeMcpGrantDialog } from "./RevokeMcpGrantDialog";

/**
 * A grant list with its states and Disconnect (ADR 0009 section 4): loading, a load failure, the
 * grid, the confirm dialog and the notice once a connection ends. The list it reads and the
 * route it revokes through are the caller's (`scope`): the person's own, an instance's or a
 * tenant's.
 */
export function McpGrantListSection({
  query,
  scope,
  showInstance,
  showUser,
  canRevoke,
  ariaLabel,
  emptyMessage,
  testId,
}: {
  query: UseQueryResult<McpGrantList<McpGrantView | McpAdminGrantView>>;
  scope: McpRevokeScope;
  showInstance: boolean;
  showUser: boolean;
  canRevoke: boolean;
  ariaLabel: string;
  emptyMessage: string;
  testId: string;
}) {
  const revoke = useRevokeMcpGrant(scope);
  const notice = useNotice();
  const revokeError = useInlineError();
  const [target, setTarget] = useState<McpGrantView | McpAdminGrantView | null>(null);

  function cancel() {
    revokeError.clear();
    setTarget(null);
  }

  function confirm(grant: McpGrantView) {
    revokeError.clear();
    revoke.mutate(grant.id, {
      onSuccess: () => {
        setTarget(null);
        notice.success(`${grant.name} is disconnected.`);
      },
      onError: (error) => revokeError.show(error, "The app was not disconnected."),
    });
  }

  if (query.isError && !query.data) {
    return (
      <SkyText as="p" size={300} color="muted" block testId={`${testId}-error`}>
        This list did not load. Reload the page to try again.
      </SkyText>
    );
  }
  if (!query.data) {
    return (
      <SkyText size={300} color="muted" testId={`${testId}-loading`}>
        Loading…
      </SkyText>
    );
  }

  const whose =
    target && "user" in target ? `${target.user.displayName ?? target.user.email}'s` : "your";

  return (
    <div className="flex flex-col gap-2">
      <ZoneCaption testId={`${testId}-zone`} />
      <McpGrantsGrid
        rows={query.data.grants}
        showInstance={showInstance}
        showUser={showUser}
        canRevoke={canRevoke}
        onRevoke={setTarget}
        ariaLabel={ariaLabel}
        emptyMessage={emptyMessage}
        testId={testId}
      />
      <RevokeMcpGrantDialog
        grant={target}
        isSubmitting={revoke.isPending}
        error={revokeError.error}
        onConfirm={confirm}
        onCancel={cancel}
        whose={whose}
      />
    </div>
  );
}
